import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { FaceId } from "~/lib/faces";
import { FACES } from "~/lib/faces";

const TOTAL_FACES = FACES.length;
const STORAGE_KEY = "jgson.visitor.v1";

interface VisitorState {
  facesVisited: FaceId[];
  trustUnlockedSeen: boolean;
  hintsDismissed: boolean;
  easterEggUnlocked: boolean;
  visit: (id: FaceId) => void;
  dismissHints: () => void;
  markTrustUnlockedSeen: () => void;
  unlockEasterEgg: () => void;
  reset: () => void;
}

export const useVisitor = create<VisitorState>()(
  persist(
    (set) => ({
      facesVisited: [],
      trustUnlockedSeen: false,
      hintsDismissed: false,
      easterEggUnlocked: false,
      visit: (id) =>
        set((s) =>
          s.facesVisited.includes(id)
            ? s
            : { facesVisited: [...s.facesVisited, id] },
        ),
      dismissHints: () =>
        set((s) => (s.hintsDismissed ? s : { hintsDismissed: true })),
      markTrustUnlockedSeen: () =>
        set((s) =>
          s.trustUnlockedSeen ? s : { trustUnlockedSeen: true },
        ),
      unlockEasterEgg: () =>
        set((s) =>
          s.easterEggUnlocked ? s : { easterEggUnlocked: true },
        ),
      reset: () =>
        set({
          facesVisited: [],
          trustUnlockedSeen: false,
          hintsDismissed: false,
          easterEggUnlocked: false,
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

interface SceneState {
  activeFaceId: FaceId | null;
  hoveredFaceId: FaceId | null;
  easterEggRevealOpen: boolean;
  setActiveFace: (id: FaceId | null) => void;
  setHoveredFace: (id: FaceId | null) => void;
  openEasterEggReveal: () => void;
  closeEasterEggReveal: () => void;
}

export const useScene = create<SceneState>((set) => ({
  activeFaceId: null,
  hoveredFaceId: null,
  easterEggRevealOpen: false,
  setActiveFace: (id) => set({ activeFaceId: id }),
  setHoveredFace: (id) => set({ hoveredFaceId: id }),
  openEasterEggReveal: () => set({ easterEggRevealOpen: true }),
  closeEasterEggReveal: () => set({ easterEggRevealOpen: false }),
}));

export function selectMeterPct(facesVisited: FaceId[]): number {
  return Math.min(100, Math.round((facesVisited.length / TOTAL_FACES) * 100));
}

export function useMeterPct(): number {
  return useVisitor((s) => selectMeterPct(s.facesVisited));
}
